import { intl, ui } from '@beelday/common'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useUnsavedChanges } from '.'

export const useHandleUnsavedRaw = (hasUnsaved: boolean): void => {
	const confirm = ui.useConfirm()
	const translate = intl.useTranslate()
	const unsavedChanges = useUnsavedChanges()

	useEffect(() => {
		if (hasUnsaved) {
			const listener: WindowEventHandlers['onbeforeunload'] = event => {
				event.preventDefault()
				event.returnValue = translate('unsaved_data.desc')
				return false
			}
			window.addEventListener('beforeunload', listener)
			return () => window.removeEventListener('beforeunload', listener)
		}
	}, [hasUnsaved, translate])

	const history = useHistory()
	// useLayoutEffect (not useEffect) so that when hasUnsaved flips to false as
	// part of a save-success state update, the stale blocker is torn down in the
	// same synchronous flush — before any code later in that same update (e.g. a
	// post-save history.push) can run into a passive effect that hasn't fired yet.
	useLayoutEffect(() => {
		if (hasUnsaved) {
			const unblock = history.block(location => {
				confirm
					.unsaved()
					.then(unblock)
					.then(() => unsavedChanges?.clear())
					.then(() => history.push(location))
				return false
			})
			return unblock
		}
	}, [hasUnsaved, unsavedChanges, confirm, history])
}

export const useHandleUnsaved = (): void => {
	const unsavedChanges = useUnsavedChanges()
	const [hasUnsaved, setHasUnsaved] = useState(
		Boolean(unsavedChanges && !unsavedChanges?.isEmpty())
	)
	useEffect(() => {
		return unsavedChanges?.onChange(setHasUnsaved)
	}, [unsavedChanges])

	useHandleUnsavedRaw(hasUnsaved)
}

export default useHandleUnsaved
